import { graphlib } from "@dagrejs/dagre";
import {
  type CompilerError,
  type Document,
  type GraphNode,
  type ParsedRuleGraph,
  type Relationship,
  type ReleaseDocument,
  type ReleaseDocuments,
  type RuleGraph,
  type ValueType,
  findNode,
  flattenReleaseDocuments,
  getAttributeDescriptionSlashMode,
  getNamespaceForDefinedIn,
  isDocumentId,
  iterateGraphNodes,
  normalizeText,
  sanitizeAttributeDescriptionSlash,
  sanitizeDocumentName,
  sanitizeNamespace,
} from "@packages/commons"; //import {RuleGraph} from "@packages/commons";
import { cloneDeep, filter } from "lodash";
import {
  AND_COMMAND,
  COMPILER_ERROR_TYPES,
  CONTACT_END_COMMAND,
  CONTACT_START_COMMAND,
  OR_COMMAND,
  VALIDATE_COMMAND,
  WARNING_COMMAND,
  escapeRegExp,
} from "@imminently/decisively-core";
import CompilerScope from "./CompilerScope";
import GraphBuilder, { extractAttributeAssignment } from "./GraphBuilder";
import { cleanGraph } from "./GraphClean";
import { type Expression, type ParseResult, generateExpressionId, getGuidForDescription, parse } from "./Parser";
import Tokenizer from "./Tokenizer";

const COMPILER_CONTEXT_SYMBOL = Symbol("CompilerContext");

const TIMES = 1;

export interface CompilerContext extends Omit<CompileOptions, "attributes" | "graph" | "documentTree"> {
  // prevents this being constructed elsewhere
  [COMPILER_CONTEXT_SYMBOL]: true;
  tokenizer: Tokenizer;
  rootScope: CompilerScope;
  relationships: Relationship[];
  namespace: string | undefined;
  enableNamespaces: boolean;
  disableGuids: boolean;
  currentDocument: Document | undefined;
  subGraph: ParsedRuleGraph;
  graph: ParsedRuleGraph;
  errors: CompilerError[];
  documents: ReleaseDocument[];
  documentTree: ReleaseDocuments | undefined;
  graphProcessed: boolean;
  dependantDocuments: Set<string>;
  guidMap: Record<string, string>;

  matchEntity: (text: string) => string | undefined;
  getNamespaceForDocumentId: (id: string) => string | undefined;
  reset: () => void;
  child: (options?: Partial<CompileOptions>) => CompilerContext;
}

interface BaseOpmStatement<T extends string> {
  type: T;
}

export interface OpmConclusion extends BaseOpmStatement<"OPM-conclusion" | `OPM-level${number}`> {
  text: string;
}

export interface OpmAlternative extends BaseOpmStatement<"OPM-Alternativeconclusion"> {}

export interface DecisionTableV2 {
  conclusion: string;
  header: {
    text: string;
  };
  subConclusions: Array<{
    text: string;
    conditions: OpmStatement[];
  }>;
  otherwise?: {
    text: string;
  };
}

export interface OpmDecisionTable extends BaseOpmStatement<"decision-table"> {
  conclusion: string;
  header: {
    text: string;
  };
  sub_conclusions: Array<{
    text: string;
    conditions: OpmStatement[];
  }>;
  otherwise?: {
    text: string;
  };
}

type OpmStatement = OpmConclusion | OpmDecisionTable | OpmAlternative;

export const PROCESSED_CHAR = String.fromCharCode(0);

export type AttributesOption = Record<string, Attribute | string> | Attribute[];

export interface CompilerContextOptions {
  enableNamespaces?: boolean;
  disableGuids?: boolean;
  debug?: boolean;
  graph?: RuleGraph;
  attributes?: AttributesOption;
  relationships?: Relationship[];
  assignableId?: string;
  documentTree?: ReleaseDocuments;
  currentDocument?: Document;
  guidMap?: Record<string, string>;
}

export type CompileOptions = (
  | {
      context: CompilerContext;
    }
  | (CompilerContextOptions & {
      context?: undefined;
    })
) & {
  currentDocument?: Document;
};

const processOperator = (operator: "and" | "or", context: CompilerContext, scope: CompilerScope): ParseResult => {
  return {
    expressions: [
      {
        id: generateExpressionId(),
        type: "command",
        command: operator === "and" ? AND_COMMAND : OR_COMMAND,
        arguments: [],
        natural: true,
        start: 0,
        end: operator.length,
      },
    ],
    errors: [],
    context: context,
    scope: new CompilerScope("operator", scope),
    attributes: [],
  };
};

type EntityMatchInfo = {
  name: string;
  relationship: Relationship;
  regexp: RegExp;
  parents: string[];
};

export interface EntityMatcher {
  matchEntity(text: string): string | undefined;
  entityInfo: EntityMatchInfo[];
}

export const createEntityMatcher = (relationships: Relationship[]): EntityMatcher => {
  const entityMatchers: EntityMatchInfo[] = [];
  if (Array.isArray(relationships)) {
    for (const relationship of relationships) {
      if (relationship.name && relationship.containment) {
        const entityRegex = createEntityRegex(relationship);
        if (entityRegex) {
          const parents: string[] = [];

          let currentRelationship: Relationship | undefined = relationship;
          while (currentRelationship?.source) {
            currentRelationship = relationships.find(
              (other) => other.target === currentRelationship?.source && other.id !== currentRelationship?.id,
            );
            if (currentRelationship?.name) {
              parents.push(currentRelationship.name);
            }
          }

          entityMatchers.push({
            relationship,
            name: relationship.name,
            regexp: entityRegex,
            parents: parents,
          });
        }
      }
    }

    entityMatchers.sort((a, b) => {
      if (a.parents.includes(b.name)) {
        return -1;
      }
      if (b.parents.includes(a.name)) {
        return 1;
      }

      return b.name.length - a.name.length;
    });
  }
  //console.log(entityMatchers.map(matcher => matcher.name));

  const matchEntity = (text: string) => {
    const matches = entityMatchers.filter(({ regexp }) => {
      return text.match(regexp);
    });
    if (matches.length) {
      matches.sort((a, b) => {
        if (a.parents.includes(b.name)) {
          return -1;
        }
        return 0;
      });

      return matches[0].name;
    }
    return undefined;
  };

  return {
    matchEntity,
    entityInfo: entityMatchers,
  };
};

export const createCompilerContext = (options: CompilerContextOptions): CompilerContext => {
  const attributes = new CompilerScope("ROOT");

  const relationships = options.relationships ?? [];
  const entityMatcher = createEntityMatcher(relationships);

  if (options.attributes) {
    if (Array.isArray(options.attributes)) {
      for (const attribute of options.attributes) {
        attributes.addAttribute({
          id: attribute.id,
          description: attribute.description && normalizeText(attribute.description),
          entity: attribute.entity,
          type: attribute.type,
          meta: attribute.meta,
        });
      }
    } else {
      for (const [key, value] of Object.entries(options.attributes)) {
        const normalizedKey = normalizeText(key).toLowerCase();
        if (typeof value === "string") {
          attributes.addAttribute({
            id: value,
            description: normalizedKey,
            entity: "global",
          });
        } else {
          attributes.addAttribute({
            id: value.id,
            description: normalizedKey,
            entity: value.entity,
            type: value.type,
            meta: value.meta,
          });
        }
      }
    }
  }

  const document = options.currentDocument;

  let graph: ParsedRuleGraph | undefined;

  if (typeof options.graph?.nodes === "function") {
    graph = cloneDeep(options.graph) as ParsedRuleGraph;
  } else if (options.graph?.nodes) {
    graph = graphlib.json.read(options.graph) as ParsedRuleGraph;
  }

  const { documentTree, guidMap, ...otherOptions } = options;

  const documents = documentTree
    ? Array.isArray(documentTree)
      ? documentTree
      : flattenReleaseDocuments(documentTree)
    : [];

  const getNamespaceForDocumentId = (id: string) => {
    if (!options.enableNamespaces || !id) {
      return undefined;
    }
    if (!options.documentTree) {
      return undefined;
    }

    return getNamespaceForDefinedIn(id, options.documentTree);
  };

  const tokenizer = new Tokenizer({
    relationships: relationships,
    scope: attributes,
  });

  const enhanceContext = (
    context: Omit<CompilerContext, "reset" | "getNamespaceForDocumentId" | "subGraph" | "graph" | "child">,
  ): CompilerContext => {
    let subGraph: ParsedRuleGraph | undefined;
    return Object.assign(context, {
      reset: () => {
        subGraph = undefined;
        context.errors = [];
      },

      getNamespaceForDocumentId: getNamespaceForDocumentId,

      get subGraph() {
        if (!subGraph) {
          subGraph = new graphlib.Graph() as ParsedRuleGraph;
        }
        return subGraph;
      },

      get graph() {
        if (!graph) {
          graph = new graphlib.Graph() as ParsedRuleGraph;
        }
        return graph;
      },
      child: (options?: Pick<CompileOptions, "currentDocument">) => {
        const child = enhanceContext({ ...context, dependantDocuments: new Set() });
        child.reset();
        if (options?.currentDocument) {
          initContextForDocument(child, options.currentDocument);
        }
        return child;
      },
    }) satisfies CompilerContext;
  };

  const context = enhanceContext({
    ...otherOptions,
    [COMPILER_CONTEXT_SYMBOL]: true,
    documents: documents,
    tokenizer: tokenizer,
    rootScope: attributes,
    relationships: relationships,
    errors: [],
    currentDocument: undefined,
    guidMap: guidMap ?? {},
    enableNamespaces: Boolean(options.enableNamespaces),
    disableGuids: Boolean(options.disableGuids),
    namespace: undefined,
    matchEntity: entityMatcher.matchEntity,
    documentTree: documentTree,
    graphProcessed: false,
    dependantDocuments: new Set(),
  });
  initContextForDocument(context, document);

  return context;
};

const initContextForDocument = (context: CompilerContext, document: Document | undefined, force?: boolean) => {
  if (!force && context.currentDocument !== undefined && document?.id === context.currentDocument?.id) {
    return;
  }

  const currentNamespace = context.enableNamespaces && document?.name ? sanitizeNamespace(document.name) : undefined;

  if (context.graph) {
    for (const nodeId of context.graph.nodes()) {
      const node = context.graph.node(nodeId);
      if (!node) {
        continue;
      }

      if (!context.graphProcessed) {
        fixNodeUsedInDefinedIn(node, context.documents);
      }
      const newDefined: string | undefined = !document || node.definedIn === document.id ? undefined : node.definedIn;

      if (!context.graphProcessed) {
        if (node?.description) {
          if (node.description.includes("/")) {
            continue;
          }

          const namespace = node.definedIn ? context.getNamespaceForDocumentId(node.definedIn) : undefined;

          const attribute = {
            id: nodeId,
            namespace: !context.enableNamespaces ? undefined : namespace,
            // reset all nodes in current namespace, they need to be redefined
            definedIn: newDefined,
            description: normalizeText(node.description),
            // recheck entities that come from the graph, we can't trust the graph, the graph lies.
            entity: (node.inferred ? node.entity : context.matchEntity(node.description)) ?? "global",
            type: node.type,
            meta: {
              sectionId: node.sectionId,
            },
          } satisfies Attribute;

          context.rootScope.addAttribute(attribute);

          // this fix only applies to input nodes, otherwise normal entity processing can clean up the entity
          if (!node.definedIn && attribute.entity !== node.entity) {
            context.graph.setNode(nodeId, {
              ...node,
              entity: attribute.entity,
            });
          }

          // inferred should be assoicated with a document
          if (!node.definedIn && node.inferred) {
            context.graph.setNode(nodeId, {
              ...node,
              inferred: undefined,
            });
          }
        }
      } else {
        const attribute = context.rootScope.getAttributeById(nodeId);
        if (attribute) {
          attribute.definedIn = newDefined;
        }
      }

      // reset all nodes in current namespace, they need to be redefined
      if (node.definedIn && document?.id === node.definedIn) {
        if (!node.description) {
          context.graph.removeNode(nodeId);
        } else {
          context.graph.setNode(nodeId, {
            ...node,
            inferred: undefined,
            definedIn: undefined,
            usedRelationships: undefined,
          });
        }
      }
    }
  }

  context.graphProcessed = true;
  context.namespace = currentNamespace;
  context.currentDocument = document;
};

export interface AttributeMeta {
  sectionId?: string;
}

export interface Attribute {
  id: string;
  description: string | undefined;
  namespace?: string;
  entity: string;
  generated?: boolean;
  type?: ValueType;
  definedIn?: string;
  /**
   * The attribute is a derivative attribute which means it should not be added to the graph because it just represents
   * a different form of an existing attribute. For example, if we have an attribute "is red" and "is not red" then
   * "is not red" is a derivative of "is red". They will share the same id but have different descriptions.
   */
  derivativeFrom?: string;
  identifier?: boolean;
  form?: "positive" | "negative";

  // added context from the compiler
  meta?: AttributeMeta;
}

const compileV2 = (context: CompilerContext) => {
  const graphBuilder = new GraphBuilder();
  const process = (declaration: any, level = 1) => {
    if (declaration.type === "conclusion") {
      const result = parse({
        input: declaration.text,
        context: context,
      });

      graphBuilder.flushPreviousConclusionToGraph(context, result);
    } else if (declaration.type === "operator") {
      // the conclusion scope
      const scope = graphBuilder.getCurrentScope();
      if (!scope) {
        throw new Error("Missing scope");
      }
      const result: ParseResult = processOperator(declaration.operator, context, scope);

      graphBuilder.addCondition(result, level);
      if (Array.isArray(declaration.children)) {
        for (const child of declaration.children) {
          process(child, level + 1);
        }
      }
    } else if (declaration.type === "condition") {
      const scope = graphBuilder.getCurrentScope();
      if (!scope) {
        throw new Error("Missing scope");
      }
      const result = parse({
        input: declaration.text,
        context: context,
        scope: scope,
      });

      graphBuilder.addCondition(result, level);
    } else if (declaration.type === "decision-table") {
      graphBuilder.flushPreviousConclusionToGraph(context);
      graphBuilder.flushDecisionTableToGraphV2(context, declaration);
    }
  };
  if (context.currentDocument) {
    // @ts-ignore
    if (context.currentDocument && Array.isArray(context.currentDocument?.contents.rules)) {
      // @ts-ignore
      for (const declaration of context.currentDocument.contents.rules) {
        process(declaration);
      }
    }
  }
  graphBuilder.flushPreviousConclusionToGraph(context);
};

const compileV3 = (context: CompilerContext) => {
  const process = (declaration: any, scope: CompilerScope, level = 1, parentGraphBuilder?: GraphBuilder) => {
    if (declaration.type === "conclusion") {
      const graphBuilder = new GraphBuilder();
      const result = parse({
        input: declaration.text,
        context: context,
        scope,
        attributeMeta: {
          sectionId: declaration.sectionId,
        },
      });

      if (result.errors.length) {
        context.errors.push(...result.errors);
        return;
      }

      if (result.expressions[0]?.type === "command") {
        const command = result.expressions[0].command;
        // RaiseError() & RaiseWarning()
        if (command.id === VALIDATE_COMMAND.id || command.id === WARNING_COMMAND.id) {
          if (Array.isArray(declaration.children)) {
            for (const child of declaration.children) {
              if (process(child, result.scope, 1, graphBuilder) === false) {
                return false;
              }
            }
          }
          graphBuilder.flushValidationToGraph(context, result);
          return;
        }

        if (command.id === CONTACT_START_COMMAND.id || command.id === CONTACT_END_COMMAND.id) {
          graphBuilder.flushContactStartOrEndToGraph(context, result, command.id === CONTACT_START_COMMAND.id);
          return;
        }
      }

      const assignable = extractAttributeAssignment(context, scope, result.expressions);
      // if there is no assignable, then we are actually just adding a condition
      if (!assignable?.attribute) {
        parentGraphBuilder?.addCondition(result, level);

        if (Array.isArray(declaration.children)) {
          for (const child of declaration.children) {
            if (process(child, scope, level + 1, parentGraphBuilder) === false) {
              return false;
            }
          }
        }
        return;
      }

      if (Array.isArray(declaration.children)) {
        for (const child of declaration.children) {
          if (process(child, result.scope, 1, graphBuilder) === false) {
            return false;
          }
        }
      }

      const attributeAssignment = graphBuilder.flushConclusionToGraph(context, result);
      if (parentGraphBuilder && attributeAssignment) {
        // add just a reference to the attribute to the parent conditions
        parentGraphBuilder.addCondition(result, level);
      }
    } else if (declaration.type === "operator") {
      if (!parentGraphBuilder) {
        context.errors.push({
          message: "Rule operator must be part of a valid conclusion",
          type: COMPILER_ERROR_TYPES.MISSING_CONCLUSION.id,
        });
        return;
      }

      const result: ParseResult = processOperator(declaration.operator, context, scope);

      parentGraphBuilder?.addCondition(result, level);
      if (Array.isArray(declaration.children)) {
        for (const child of declaration.children) {
          if (process(child, scope, level + 1, parentGraphBuilder) === false) {
            return false;
          }
        }
      }
    } else if (declaration.type === "condition") {
      if (!parentGraphBuilder) {
        context.errors.push({
          message: `Rule condition '${declaration.text}' must be part of a valid conclusion`,
          type: COMPILER_ERROR_TYPES.MISSING_CONCLUSION.id,
        });
        return;
      }

      const result = parse({
        input: declaration.text,
        context: context,
        scope: scope,
      });
      if (result.errors.length) {
        context.errors.push(...result.errors);
        return false;
      }

      parentGraphBuilder.addCondition(result, level);

      if (Array.isArray(declaration.children)) {
        for (const child of declaration.children) {
          if (process(child, scope, level + 1, parentGraphBuilder) === false) {
            return false;
          }
        }
      }
    } else if (declaration.type === "decision-table") {
      const graphBuilder = new GraphBuilder();
      graphBuilder.flushDecisionTableToGraphV2(context, declaration);
    }
  };
  // @ts-ignore
  if (context.currentDocument && Array.isArray(context.currentDocument?.contents.rules)) {
    // @ts-ignore
    for (const declaration of context.currentDocument.contents.rules) {
      process(declaration, context.rootScope);
    }
  }
};

export interface ConclusionLevel {
  depth: number;
  parseResult: ParseResult;
  children: ConclusionLevel[];
}

const compileV1 = (context: CompilerContext) => {
  const graphBuilder = new GraphBuilder();

  if (context.currentDocument) {
    if (context.currentDocument && Array.isArray(context.currentDocument?.contents)) {
      for (const declaration of context.currentDocument.contents) {
        if (declaration.type === "OPM-conclusion" || declaration.type.startsWith("OPM-level")) {
          if (declaration.type === "OPM-level" && !graphBuilder.getCurrentScope()) {
            // we are in a discarded conclusion
            continue;
          }
          const scope = declaration.type === "OPM-conclusion" ? context.rootScope : graphBuilder.getCurrentScope();
          const result = parse({
            input: (declaration as OpmConclusion).text,
            context: context,
            scope: scope,
          });
          if (result.errors.length) {
            context.errors.push(...result.errors);
            graphBuilder.discardPreviousConclusion();
            continue;
          }

          if (declaration.type.startsWith("OPM-level")) {
            const level = Number.parseInt(declaration.type.replace("OPM-level", ""));
            graphBuilder.addCondition(result, level);
          } else {
            graphBuilder.flushPreviousConclusionToGraph(context, result);
          }
        } else if (declaration.type === "decision-table") {
          graphBuilder.flushPreviousConclusionToGraph(context);
          graphBuilder.flushDecisionTableToGraphV1(context, declaration);
        }
      }
    }
  }
  graphBuilder.flushPreviousConclusionToGraph(context);
};

/**
 * 1. lex the document
 * 2. parse the tokens into expressions
 * 3. build the graph
 */
export const compile = (options: CompileOptions): CompilerContext => {
  let context = options.context ? options.context : createCompilerContext(options);

  if (options.context) {
    context = options.context.child({
      currentDocument: options.currentDocument,
    });
  }
  if (!context.currentDocument) {
    throw new Error("Missing document for compile");
  }

  // create/sanitize identifier nodes
  for (const relationship of context.relationships) {
    if (relationship.name && relationship.containment) {
      const rawNodeDescription = relationship.textSingular;
      if (rawNodeDescription) {
        const slashResult = sanitizeAttributeDescriptionSlash(
          rawNodeDescription,
          getAttributeDescriptionSlashMode(context.disableGuids),
        );
        if (slashResult.error) {
          context.errors.push(slashResult.error);
          continue;
        }
        const nodeDescription = slashResult.description;

        const node = findNode(context.graph, context.documentTree, nodeDescription);
        if (!node) {
          const id = getGuidForDescription(context, nodeDescription);
          context.graph.setNode(id, {
            id: id,
            identifier: true,
            entity: relationship.name,
            type: "auto",
            description: nodeDescription,
          });
          context.rootScope.addAttribute({
            id: id,
            description: nodeDescription,
            entity: relationship.name,
            identifier: true,
          });
        } else {
          node.identifier = true;
          context.graph.setNode(node.id, node);
        }

        const attribute = context.rootScope.getAttributeByDescription(nodeDescription, context.namespace);
        if (!attribute) {
          context.rootScope.addAttribute({
            id: getGuidForDescription(context, nodeDescription),
            description: nodeDescription,
            entity: relationship.name,
            identifier: true,
          });
        } else {
          attribute.identifier = true;
        }
      }
    }
  }

  // we need 2 passes because for namespacing we don't want to resolve to attributes in other namespaces by accident
  // if the definitions come after the usage
  const passes = context.enableNamespaces ? 2 : 1;
  for (let i = 0; i < passes; i++) {
    if (i > 0) {
      context.reset();
      initContextForDocument(context, context.currentDocument, true);
    }

    if (context.currentDocument.version === 2) {
      compileV2(context);
    } else if (context.currentDocument.version === 3) {
      compileV3(context);
    } else {
      compileV1(context);
    }

    if (context.errors.length) {
      break;
    }

    if (context.documentTree) {
      const removed = cleanGraph(context.graph, context.subGraph, context.currentDocument.id, context.documentTree, context.relationships);
      for (const removedNode of removed) {
        if (context.enableNamespaces) {
          if (removedNode.usedIn) {
            for (const usedIn of removedNode.usedIn) {
              context.dependantDocuments.add(usedIn);
            }
          }

          if (removedNode?.description) {
            // if there are also nodes with the same name defined in other documents, we need to mark them as dependent documents
            const others = filter(context.graph._nodes, (other) =>
              Boolean(
                other.id !== removedNode.id &&
                  other.definedIn &&
                  other.description &&
                  normalizeText(other.description) === removedNode.description,
              ),
            );
            for (const other of others) {
              if (other.usedIn) {
                for (const usedIn of other.usedIn) {
                  context.dependantDocuments.add(usedIn);
                }
              }
            }
          }
        }
        context.rootScope.removeAttributeById(removedNode.id);
      }
    }
  }

  postCompile(context);

  // `usedAttributes` is transient compile metadata used during cleanGraph only.
  // Do not leak it to persisted graph payloads.
  const subGraphValue = (context.subGraph.graph() || {}) as any;
  if (subGraphValue.usedAttributes) {
    const { usedAttributes, ...rest } = subGraphValue;
    context.subGraph.setGraph(Object.keys(rest).length ? rest : undefined);
  }

  context.dependantDocuments.delete(context.currentDocument.id);

  return context;
};

const markGoalNodes = (graph: ParsedRuleGraph) => {
  iterateGraphNodes(graph, (node) => {
    node.goal =
      !node.fk && !node.inferred && !node.meta && !node.identifier && !graph.predecessors(node.id)?.length
        ? true
        : undefined;
  });
};

export const postCompile = (context: CompilerContext) => {
  // mark all nodes with no predecessors as goals
  markGoalNodes(context.graph);
  markGoalNodes(context.subGraph);
};

export const createEntityRegex = (relationship: Relationship): RegExp | undefined => {
  if (!relationship.textSingular) {
    return undefined;
  }
  return new RegExp(`\\b${normalizeText(escapeRegExp(relationship.textSingular))}('s)?(?!s)\\b`, "gi");
};

export const iterateExpressions = (expressions: Expression[], func: (expression: Expression) => void | false) => {
  for (const expression of expressions) {
    if (func(expression) === false) {
      return false;
    }
    if ("arguments" in expression) {
      if (iterateExpressions(expression.arguments, func) === false) {
        return false;
      }
    }
  }
};

export const fixNodeUsedInDefinedIn = (node: GraphNode, documents: ReleaseDocument[]) => {
  if (node.definedIn && !isDocumentId(node.definedIn)) {
    const documentName = sanitizeDocumentName(node.definedIn);
    const document = documents.find((doc) => sanitizeDocumentName(doc.name) === documentName);
    if (document?.reference) {
      node.definedIn = document.reference;
    } else {
      node.definedIn = undefined;
    }
  }

  if (Array.isArray(node.usedIn)) {
    node.usedIn = node.usedIn.reduce((result, usedIn: string) => {
      if (!isDocumentId(usedIn)) {
        const documentName = sanitizeDocumentName(usedIn);
        const document = documents.find((doc) => sanitizeDocumentName(doc.name) === documentName);
        if (document?.reference) {
          result.push(document.reference);
        } else {
          //console.error(`Could not find document for ${documentName} in usedIn for ${node.id}`);
        }
      } else {
        result.push(usedIn);
      }
      return result;
    }, [] as string[]);
  }
};

export const iterateDocumentContent = (contents: any, func: (content: any, path: (string | number)[]) => void | false) => {
  const processElement = (element: any, elementPath: (string | number)[]) => {
    if (!element) {
      return true;
    }

    if (Array.isArray(element)) {
      for (let i = 0; i < element.length; i++) {
        if (!processElement(element[i], [...elementPath, i])) {
          return false;
        }
      }
      return true;
    }

    if (func(element, elementPath) === false) {
      return false;
    }

    // Handle nested children
    if (element.children) {
      if (!processElement(element.children, [...elementPath, "children"])) {
        return false;
      }
    }

    // Handle decision table components
    if (element.type === "decision-table") {
      // Process header
      if (!processElement(element.header, [...elementPath, "header"])) {
        return false;
      }

      // Process conditions
      if (!processElement(element.conditions, [...elementPath, "conditions"])) {
        return false;
      }

      // Process subConclusions
      if (Array.isArray(element.subConclusions)) {
        for (let j = 0; j < element.subConclusions.length; j++) {
          const subConclusion = element.subConclusions[j];
          if (!processElement(subConclusion, [...elementPath, "subConclusions", j])) {
            return false;
          }
          // Handle conditions within subConclusions
          if (!processElement(subConclusion.conditions, [...elementPath, "subConclusions", j, "conditions"])) {
            return false;
          }
        }
      }

      // Process otherwise clause
      if (!processElement(element.otherwise, [...elementPath, "otherwise"])) {
        return false;
      }
    }

    return true;
  };

  return processElement(contents, []);
}
